/**
 * Staffology Design System — Primitives v2.0
 * -------------------------------------------------------------------
 * Six base components migrated from JSX (v1.1) to TypeScript +
 * styled-components, matching the production stack:
 *   React + Vite + styled-components + TypeScript.
 *
 * All components consume tokens via CSS variables (var(--token-name))
 * imported from `colors_and_type.css`. If the dev prefers theme
 * injection via ThemeProvider, swap `var(--x)` for
 * `${({ theme }) => theme.x}` — `theme.ts` exports the same shape.
 *
 * Components included:
 *   - Button       Primary / Secondary / Tertiary / Danger
 *   - Input        With optional left and right slots
 *   - Card         Default and elevated variants
 *   - Badge        Status pill with dot
 *   - Avatar       With image, initials, or fallback
 *   - Icon         Lucide-react wrapper with size scale
 *   - VisuallyHidden  Text for assistive tech only (the clip pattern)
 *
 *   Layout primitives (added in patch):
 *   - Stack        Flex container, row or column
 *   - Grid         CSS grid with column count or template
 *   - Container    Max-width centered wrapper
 * -------------------------------------------------------------------
 */

import React, {forwardRef} from 'react'
import styled, {css, keyframes} from 'styled-components'
import * as LucideIcons from 'lucide-react'

/* ==================================================================
   BUTTON
   ================================================================== */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'danger-filled'
  | 'warning'
  | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'
export type ButtonShape = 'default' | 'pill'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Corner shape. 'pill' rounds the ends fully (var(--radius-pill)); 'default' keeps var(--radius-sm). */
  shape?: ButtonShape
  loading?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  fullWidth?: boolean
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`

export interface SpinnerProps {
  /** Pixel size of the spinner. Defaults to 14. */
  size?: number
}

export const Spinner = styled.span<{size?: number}>`
  display: inline-block;
  width: ${({size = 14}) => size}px;
  height: ${({size = 14}) => size}px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 600ms linear infinite;
`

const buttonSizeStyles = {
  sm: css`
    height: var(--btn-height-sm);
    min-height: var(--btn-height-sm);
    padding: 0 var(--space-3);
    font-size: 13px;
    gap: var(--space-1);
  `,
  md: css`
    height: var(--btn-height-md);
    min-height: var(--touch-min);
    padding: 0 var(--space-4);
    font-size: 14px;
    gap: 6px;
  `,
  lg: css`
    height: var(--btn-height-lg);
    min-height: var(--touch-min);
    padding: 0 var(--space-5);
    font-size: 15px;
    gap: var(--space-2);
  `,
}

const buttonVariantStyles = {
  primary: css`
    /* CTA uses brand-700 (interaction), not brand-500 (identity):
       white text on #0277B6 = 4.86:1 (WCAG 2.2 AA). */
    background: var(--brand-cta);
    color: var(--text-on-brand);
    border-color: var(--brand-cta);
    box-shadow: 0 1px 0 rgba(1, 86, 138, 0.25) inset, 0 1px 2px rgba(2, 119, 182, 0.18);
    &:hover:not(:disabled) {
      background: var(--brand-cta-hover);
      border-color: var(--brand-cta-hover);
    }
    &:active:not(:disabled) {
      background: var(--brand-cta-active);
    }
  `,
  secondary: css`
    background: var(--bg-card);
    color: var(--neutral-800);
    border-color: var(--border-default);
    &:hover:not(:disabled) {
      background: var(--bg-hover);
      border-color: var(--border-strong);
    }
  `,
  tertiary: css`
    /* Text uses brand-700 so small label text passes 4.5:1. */
    background: transparent;
    color: var(--brand-cta);
    border-color: transparent;
    &:hover:not(:disabled) {
      background: var(--brand-50);
      color: var(--brand-cta-hover);
    }
  `,
  danger: css`
    background: var(--bg-card);
    color: var(--error-800);
    border-color: var(--error-100);
    &:hover:not(:disabled) {
      background: var(--error-100);
    }
  `,
  'danger-filled': css`
    background: var(--error-500);
    color: var(--text-on-brand);
    border-color: var(--error-500);
    &:hover:not(:disabled) {
      background: var(--error-800);
      border-color: var(--error-800);
    }
    &:active:not(:disabled) {
      background: var(--error-800);
    }
  `,
  warning: css`
    background: var(--warning-500);
    color: var(--text-on-brand);
    border-color: var(--warning-500);
    &:hover:not(:disabled) {
      background: var(--warning-800);
      border-color: var(--warning-800);
    }
    &:active:not(:disabled) {
      background: var(--warning-800);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--neutral-700);
    border-color: transparent;
    &:hover:not(:disabled) {
      background: var(--bg-hover);
    }
  `,
}

const StyledButton = styled.button<{
  $variant: ButtonVariant
  $size: ButtonSize
  $shape: ButtonShape
  $fullWidth: boolean
}>`
  font-family: var(--font-sans);
  font-weight: var(--fw-medium);
  letter-spacing: var(--ls-normal);
  border: 1px solid transparent;
  /* Any shape other than 'pill' (including an unknown value) keeps the default radius. */
  border-radius: ${({$shape}) => ($shape === 'pill' ? 'var(--radius-pill)' : 'var(--radius-sm)')};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all var(--motion-fast) var(--motion-ease);
  white-space: nowrap;
  user-select: none;
  ${({$fullWidth}) => $fullWidth && 'width: 100%;'}

  ${({$size}) => buttonSizeStyles[$size]};
  ${({$variant}) => buttonVariantStyles[$variant]};

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      shape = 'default',
      loading = false,
      iconLeft,
      iconRight,
      fullWidth = false,
      children,
      disabled,
      ...rest
    },
    ref,
  ) => (
    <StyledButton
      ref={ref}
      $variant={variant}
      $size={size}
      $shape={shape}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}>
      {loading ? <Spinner /> : iconLeft}
      {children}
      {!loading && iconRight}
    </StyledButton>
  ),
)
Button.displayName = 'Button'

/* ==================================================================
   INPUT
   Always pair with a <label> above the input. No floating labels.
   ================================================================== */

type InputSize = 'sm' | 'md' | 'lg'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize
  error?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
}

// Field height lives on the InputWrapper (border-box) so the OUTER field equals the token —
// matching the DS Select shell. (Previously the token sat on the inner <input>, and the
// wrapper's 1px borders added 2px, so inputs rendered 2px TALLER than selects everywhere.)
const inputWrapperHeight = {
  sm: 'var(--input-height-sm)',
  md: 'var(--input-height-md)',
  lg: 'var(--input-height-lg)',
}

const inputSizeStyles = {
  sm: css`
    font-size: 13px;
  `,
  md: css`
    font-size: 14px;
  `,
  lg: css`
    font-size: 15px;
  `,
}

const InputWrapper = styled.div<{$error: boolean; $size: InputSize}>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  box-sizing: border-box;
  height: ${({$size}) => inputWrapperHeight[$size]};
  padding: 0 var(--space-3);
  background: var(--bg-card);
  /* Input border IS the indicator (no shadow) → --border-default (3:1). */
  border: 1px solid ${({$error}) => ($error ? 'var(--error-500)' : 'var(--border-default)')};
  border-radius: var(--radius-sm);
  transition: border-color var(--motion-fast) var(--motion-ease),
    box-shadow var(--motion-fast) var(--motion-ease);

  &:hover {
    border-color: ${({$error}) => ($error ? 'var(--error-500)' : 'var(--border-strong)')};
  }

  &:focus-within {
    border-color: var(--focus-border);
    box-shadow: var(--focus-ring);
  }
`

const StyledInput = styled.input<{$size: InputSize}>`
  ${({$size}) => inputSizeStyles[$size]};
  height: 100%;
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  color: var(--text-primary);
  letter-spacing: var(--ls-normal);

  &::placeholder {
    color: var(--text-placeholder);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--text-disabled);
  }

  /*
   * Chrome paints autofilled inputs with its own background that only covers
   * the input's content box — not the wrapper's horizontal padding — leaving a
   * partial blue patch with white gutters. Cover that background with an inset
   * shadow matching the field surface so the whole field reads as one uniform
   * color, and use a long bg transition so Chrome's yellow/blue never flashes.
   */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--bg-card) inset;
    -webkit-text-fill-color: var(--text-primary);
    caret-color: var(--text-primary);
    transition: background-color 9999s ease-in-out 0s;
  }
`

const InputSlot = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
  flex-shrink: 0;
`

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({size = 'md', error = false, iconLeft, iconRight, className, style, ...rest}, ref) => (
    <InputWrapper $error={error} $size={size} className={className} style={style}>
      {iconLeft && <InputSlot>{iconLeft}</InputSlot>}
      <StyledInput ref={ref} $size={size} aria-invalid={error || undefined} {...rest} />
      {iconRight && <InputSlot>{iconRight}</InputSlot>}
    </InputWrapper>
  ),
)
Input.displayName = 'Input'

/* ==================================================================
   CARD
   ================================================================== */

type CardVariant = 'default' | 'elevated'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  padding?: keyof CardPaddingMap
}

type CardPaddingMap = {
  none: '0'
  sm: '12px'
  md: '16px'
  lg: '24px'
}

const cardPaddingMap: CardPaddingMap = {
  none: '0',
  sm: '12px',
  md: '16px',
  lg: '24px',
}

const StyledCard = styled.div<{
  $variant: CardVariant
  $padding: keyof CardPaddingMap
}>`
  background: var(--bg-card);
  /* Card is indicated by shadow + bg differential, so the border is
     decorative → --border-subtle (3:1 met by the shadow, not the border). */
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-sizing: border-box;
  padding: ${({$padding}) => cardPaddingMap[$padding]};
  box-shadow: ${({$variant}) =>
    $variant === 'elevated' ? 'var(--shadow-elevated)' : 'var(--shadow-regular)'};
  transition: box-shadow var(--motion-fast) var(--motion-ease);
`

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({variant = 'default', padding = 'md', children, ...rest}, ref) => (
    <StyledCard ref={ref} $variant={variant} $padding={padding} {...rest}>
      {children}
    </StyledCard>
  ),
)
Card.displayName = 'Card'

/* ==================================================================
   BADGE
   Status pill with optional dot. Six semantic variants.
   ================================================================== */

export type BadgeVariant =
  | 'brand'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'pending'
  | 'neutral'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  dot?: boolean
  /** Filled treatment: solid --500 background + white text. Use when the badge
   *  sits on a surface tinted the same as its soft variant (e.g. an amber badge
   *  on an amber card) so it doesn't blend in. */
  solid?: boolean
  children: React.ReactNode
  /** Optional click handler. When provided, the badge renders as role="button". */
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void
  /** Disable the badge (only applies when onClick is provided). */
  disabled?: boolean
}

const badgeVariantStyles = {
  brand: css`
    background: var(--brand-100);
    color: var(--brand-active);
    --badge-dot: var(--brand);
  `,
  success: css`
    background: var(--success-100);
    color: var(--success-800);
    --badge-dot: var(--success-500);
  `,
  warning: css`
    background: var(--warning-100);
    color: var(--warning-800);
    --badge-dot: var(--warning-500);
  `,
  error: css`
    background: var(--error-100);
    color: var(--error-800);
    --badge-dot: var(--error-500);
  `,
  info: css`
    background: var(--info-100);
    color: var(--info-800);
    --badge-dot: var(--info-500);
  `,
  pending: css`
    background: var(--pending-100);
    color: var(--pending-800);
    --badge-dot: var(--pending-500);
  `,
  neutral: css`
    background: var(--neutral-200);
    color: var(--neutral-800);
    --badge-dot: var(--neutral-600);
  `,
}

const badgeSolidStyles = {
  /* Solid brand badge carries white text → interaction cyan (4.86:1), not identity. */
  brand: css`
    background: var(--brand-cta);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
  success: css`
    background: var(--success-500);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
  warning: css`
    background: var(--warning-500);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
  error: css`
    background: var(--error-500);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
  info: css`
    background: var(--info-500);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
  pending: css`
    background: var(--pending-500);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
  neutral: css`
    background: var(--neutral-600);
    color: var(--text-on-brand);
    --badge-dot: var(--text-on-brand);
  `,
}

const StyledBadge = styled.span<{
  $variant: BadgeVariant
  $solid?: boolean
  $clickable?: boolean
  $disabled?: boolean
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--space-1) 10px;
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  ${({$variant, $solid}) => ($solid ? badgeSolidStyles[$variant] : badgeVariantStyles[$variant])};

  ${({$clickable, $disabled}) =>
    $clickable &&
    css`
      cursor: ${$disabled ? 'not-allowed' : 'pointer'};
      opacity: ${$disabled ? 0.5 : 1};
      transition: filter 0.15s ease;
      border: none;

      &:hover {
        ${!$disabled && 'filter: brightness(0.93);'}
      }
      &:focus-visible {
        outline: 2px solid var(--brand-cta);
        outline-offset: 2px;
      }
    `}
`

/* Children live in a shrinkable span so a long label ellipsis-truncates when the
   badge is width-constrained (e.g. a role name in a narrow table cell) instead of
   overflowing — text-overflow can't act on the flex container's raw text. No change
   when unconstrained (count/short badges size to content). */
const BadgeLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const BadgeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--badge-dot);
  flex-shrink: 0;
`

export const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  dot = true,
  solid = false,
  children,
  onClick,
  disabled,
  ...rest
}) => {
  const isClickable = !!onClick

  return (
    <StyledBadge
      $variant={variant}
      $solid={solid}
      $clickable={isClickable}
      $disabled={disabled}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable && !disabled ? 0 : undefined}
      aria-disabled={isClickable && disabled ? true : undefined}
      onClick={!disabled ? onClick : undefined}
      onKeyDown={
        isClickable && !disabled
          ? (e: React.KeyboardEvent<HTMLSpanElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>)
              }
            }
          : undefined
      }
      {...rest}>
      {dot && <BadgeDot />}
      <BadgeLabel>{children}</BadgeLabel>
    </StyledBadge>
  )
}

/* ==================================================================
   ROLE BADGE (Patch 3)
   Role and Status are different concepts that appear in the same
   row, so RoleBadge is deliberately its own component — not a Badge
   variant. One tint per role category; no `dot` (that's a status
   affordance). Tokens live in colors_and_type.css (--role-*).
   ================================================================== */

export type RoleBadgeVariant = 'agency' | 'msp' | 'platformAdmin' | 'company' | 'client'

export interface RoleBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  role: RoleBadgeVariant
  children: React.ReactNode
}

const roleBadgeVariantStyles = {
  agency: css`
    background: var(--role-agency-bg);
    color: var(--role-agency-fg);
  `,
  msp: css`
    background: var(--role-msp-bg);
    color: var(--role-msp-fg);
  `,
  platformAdmin: css`
    background: var(--role-platformadmin-bg);
    color: var(--role-platformadmin-fg);
  `,
  company: css`
    background: var(--role-company-bg);
    color: var(--role-company-fg);
  `,
  client: css`
    background: var(--role-client-bg);
    color: var(--role-client-fg);
  `,
}

const StyledRoleBadge = styled.span<{$role: RoleBadgeVariant}>`
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) 10px;
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  letter-spacing: 0;
  white-space: nowrap;
  ${({$role}) => roleBadgeVariantStyles[$role]};
`

export const RoleBadge: React.FC<RoleBadgeProps> = ({role, children, ...rest}) => (
  <StyledRoleBadge $role={role} {...rest}>
    {children}
  </StyledRoleBadge>
)

/**
 * Maps a role category (as it comes from the API / ROLE_CATEGORIES) to a
 * RoleBadge variant. Returns null for an unknown/missing category so the
 * caller can skip rendering a badge. Extend this if new categories appear.
 */
export function roleBadgeVariantFromCategory(category?: string | null): RoleBadgeVariant | null {
  switch (category) {
    case 'Agency':
      return 'agency'
    case 'MSP':
      return 'msp'
    case 'Platform':
      return 'platformAdmin'
    case 'Company':
      return 'company'
    case 'Candidate':
      return 'client'
    default:
      return null
  }
}

/* ==================================================================
   AVATAR
   Image, initials, or icon fallback. Logged-in user avatar always
   navy; other avatars use a deterministic palette based on a hash
   of the name.
   ================================================================== */

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'
type AvatarColor = 'blue' | 'amber' | 'purple' | 'coral' | 'teal' | 'pink' | 'navy'

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: AvatarSize
  src?: string
  alt?: string
  initials?: string
  color?: AvatarColor
  /** When true, forces the navy palette regardless of color. Use for the logged-in user avatar. */
  isUser?: boolean
}

const avatarSizeMap: Record<AvatarSize, {size: string; fontSize: string}> = {
  sm: {size: '28px', fontSize: '11px'},
  md: {size: '36px', fontSize: '12px'},
  lg: {size: '48px', fontSize: '14px'},
  xl: {size: '64px', fontSize: '18px'},
}

const avatarColorMap: Record<AvatarColor, {bg: string; fg: string}> = {
  blue: {bg: 'var(--av-blue-bg)', fg: 'var(--av-blue-fg)'},
  amber: {bg: 'var(--av-amber-bg)', fg: 'var(--av-amber-fg)'},
  purple: {bg: 'var(--av-purple-bg)', fg: 'var(--av-purple-fg)'},
  coral: {bg: 'var(--av-coral-bg)', fg: 'var(--av-coral-fg)'},
  teal: {bg: 'var(--av-teal-bg)', fg: 'var(--av-teal-fg)'},
  pink: {bg: 'var(--av-pink-bg)', fg: 'var(--av-pink-fg)'},
  navy: {bg: 'var(--primary-700)', fg: 'var(--text-on-brand)'},
}

const StyledAvatar = styled.div<{
  $size: AvatarSize
  $color: AvatarColor
}>`
  width: ${({$size}) => avatarSizeMap[$size].size};
  height: ${({$size}) => avatarSizeMap[$size].size};
  font-size: ${({$size}) => avatarSizeMap[$size].fontSize};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: var(--font-sans);
  font-weight: var(--fw-semibold);
  background: ${({$color}) => avatarColorMap[$color].bg};
  color: ${({$color}) => avatarColorMap[$color].fg};
  overflow: hidden;
  letter-spacing: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

/**
 * Deterministic color from a string. Use this to pick a stable
 * avatar color from a user's name.
 */
export function avatarColorFromName(name: string): AvatarColor {
  const colors: AvatarColor[] = ['blue', 'amber', 'purple', 'coral', 'teal', 'pink']
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i) // eslint-disable-line no-bitwise
    hash |= 0 // eslint-disable-line no-bitwise
  }
  return colors[Math.abs(hash) % colors.length]
}

export const Avatar: React.FC<AvatarProps> = ({
  size = 'md',
  src,
  alt,
  initials,
  color = 'blue',
  isUser = false,
  ...rest
}) => {
  const finalColor: AvatarColor = isUser ? 'navy' : color
  // Fall back to initials when there is no src OR the image fails to load
  // (broken/empty URL). Without this a bad src renders the browser's
  // broken-image icon + alt text instead of a clean initial.
  const [imgFailed, setImgFailed] = React.useState(false)
  React.useEffect(() => {
    setImgFailed(false)
  }, [src])
  const showImage = Boolean(src) && !imgFailed
  return (
    <StyledAvatar
      $size={size}
      $color={finalColor}
      role="img"
      aria-label={alt ?? (initials ? `Avatar for ${initials}` : 'Avatar')}
      {...rest}>
      {showImage ? <img src={src} alt={alt ?? ''} onError={() => setImgFailed(true)} /> : initials}
    </StyledAvatar>
  )
}

/* ==================================================================
   ICON
   Wrapper around lucide-react with size scale. Provides a single
   place to control icon sizing without scattering pixel values
   throughout the codebase.
   ================================================================== */

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const iconSizeMap: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 48,
  xl: 56,
}

type LucideIconName = keyof typeof LucideIcons

export interface IconProps {
  name: LucideIconName
  size?: IconSize
  color?: string
  strokeWidth?: number
  className?: string
  'aria-label'?: string
  'aria-hidden'?: boolean
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 'sm',
  color = 'currentColor',
  strokeWidth = 1.5,
  className,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}) => {
  const LucideComponent = LucideIcons[name] as React.ComponentType<{
    size: number
    color: string
    strokeWidth: number
    className?: string
    'aria-label'?: string
    'aria-hidden'?: boolean
  }>

  if (!LucideComponent) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`<Icon>: lucide-react icon "${String(name)}" not found.`)
    }
    return null
  }

  return (
    <LucideComponent
      size={iconSizeMap[size]}
      color={color}
      strokeWidth={strokeWidth}
      className={className}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden ?? !ariaLabel}
    />
  )
}

/* ==================================================================
   LAYOUT PRIMITIVES (patch — added to v2.0)
   ------------------------------------------------------------------
   Stack, Grid, and Container replace any usage of MUI Grid or
   ad-hoc flex/grid styled-components throughout the app. They
   consume spacing tokens directly so layouts stay consistent.
   ================================================================== */

type SpaceKey =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '8'
  | '10'
  | '12'
  | '14'
  | '16'
  | '20'
  | '24'
  | '32'

const space = (key: SpaceKey): string => (key === '0' ? '0' : `var(--space-${key})`)

/* ------------------------------------------------------------------
   STACK
   Flex container in row or column direction, with token-based gap.
   ------------------------------------------------------------------ */

type StackDirection = 'row' | 'column'
type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline'
type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'

const alignMap: Record<StackAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
}

const justifyMap: Record<StackJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
}

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: StackDirection
  gap?: SpaceKey
  align?: StackAlign
  justify?: StackJustify
  wrap?: boolean
  fullWidth?: boolean
  as?: keyof JSX.IntrinsicElements
}

const StyledStack = styled.div<{
  $direction: StackDirection
  $gap: SpaceKey
  $align: StackAlign
  $justify: StackJustify
  $wrap: boolean
  $fullWidth: boolean
}>`
  display: flex;
  flex-direction: ${({$direction}) => $direction};
  gap: ${({$gap}) => space($gap)};
  align-items: ${({$align}) => alignMap[$align]};
  justify-content: ${({$justify}) => justifyMap[$justify]};
  flex-wrap: ${({$wrap}) => ($wrap ? 'wrap' : 'nowrap')};
  ${({$fullWidth}) => $fullWidth && 'width: 100%;'}
`

export const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'column',
      gap = '4',
      align = 'stretch',
      justify = 'start',
      wrap = false,
      fullWidth = false,
      children,
      ...rest
    },
    ref,
  ) => (
    <StyledStack
      ref={ref}
      $direction={direction}
      $gap={gap}
      $align={align}
      $justify={justify}
      $wrap={wrap}
      $fullWidth={fullWidth}
      {...rest}>
      {children}
    </StyledStack>
  ),
)
Stack.displayName = 'Stack'

/* ------------------------------------------------------------------
   GRID
   CSS grid container. `columns` accepts either a number (repeat
   N equal columns) or a template string (e.g. '1fr 2fr 1fr').
   ------------------------------------------------------------------ */

type GridAlign = 'start' | 'center' | 'end' | 'stretch'

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: number | string
  gap?: SpaceKey
  rowGap?: SpaceKey
  columnGap?: SpaceKey
  alignItems?: GridAlign
}

const StyledGrid = styled.div<{
  $columns: string
  $gap?: SpaceKey
  $rowGap?: SpaceKey
  $columnGap?: SpaceKey
  $alignItems: GridAlign
}>`
  display: grid;
  grid-template-columns: ${({$columns}) => $columns};
  align-items: ${({$alignItems}) => alignMap[$alignItems]};

  ${({$gap}) => $gap !== undefined && `gap: ${space($gap)};`}
  ${({$rowGap}) => $rowGap !== undefined && `row-gap: ${space($rowGap)};`}
  ${({$columnGap}) => $columnGap !== undefined && `column-gap: ${space($columnGap)};`}
`

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {columns = 12, gap = '4', rowGap, columnGap, alignItems = 'stretch', children, ...rest},
    ref,
  ) => {
    const template = typeof columns === 'number' ? `repeat(${columns}, minmax(0, 1fr))` : columns
    return (
      <StyledGrid
        ref={ref}
        $columns={template}
        $gap={rowGap === undefined && columnGap === undefined ? gap : undefined}
        $rowGap={rowGap}
        $columnGap={columnGap}
        $alignItems={alignItems}
        {...rest}>
        {children}
      </StyledGrid>
    )
  },
)
Grid.displayName = 'Grid'

/* ------------------------------------------------------------------
   CONTAINER
   Max-width centered wrapper for page-level content.
   ------------------------------------------------------------------ */

type ContainerMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const containerMaxWidthMap: Record<ContainerMaxWidth, string> = {
  sm: 'var(--container-sm)',
  md: 'var(--container-md)',
  lg: 'var(--container-lg)',
  xl: 'var(--container-xl)',
  full: '100%',
}

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  maxWidth?: ContainerMaxWidth
  padding?: SpaceKey
  centered?: boolean
}

const StyledContainer = styled.div<{
  $maxWidth: ContainerMaxWidth
  $padding: SpaceKey
  $centered: boolean
}>`
  width: 100%;
  max-width: ${({$maxWidth}) => containerMaxWidthMap[$maxWidth]};
  padding-left: ${({$padding}) => space($padding)};
  padding-right: ${({$padding}) => space($padding)};
  ${({$centered}) => $centered && 'margin-left: auto; margin-right: auto;'}
`

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({maxWidth = 'lg', padding = '6', centered = true, children, ...rest}, ref) => (
    <StyledContainer
      ref={ref}
      $maxWidth={maxWidth}
      $padding={padding}
      $centered={centered}
      {...rest}>
      {children}
    </StyledContainer>
  ),
)
Container.displayName = 'Container'

/* ==================================================================
   VISUALLY HIDDEN
   Text that assistive tech reads but the page does not show: a
   label for an icon-only control, a chart's text alternative, a
   heading the visual design leaves out. Uses the same clip pattern
   as the Checkbox and Radio native inputs. Render another element
   with `as` (e.g. as="h2", as="ul").
   ================================================================== */

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`
